import { Injectable } from "@angular/core";
import { UserPermissionsException } from "src/app/core/exceptions/user-permissions-exception";
import { UsuarioPerfilResponse } from "src/app/core/response/usuario-perfil/usuario-perfil-response";
import { CognitoUserSession } from "src/app/core/types/cognito-user-session";
import { urlApi } from "src/environments/environment";
import { ActionMenuPermissionsEnum } from "../../enum/action-menu-permissions-enum";
import { Router } from "@angular/router";
import { EventEmitterBaseService } from "../event-emitter-service/event-emitter.service";
import { AppEvents } from "../../types/app-events";
import { SistemaEnum } from "../../enum/sistema-enum";
import { MenuSistemaEnum } from "../../enum/menu-sistema-enum";
import { HttpService } from "src/app/services/http.service";
import { FactoryService } from "../factory/factory.service";

@Injectable({ providedIn: "root" })
export class SessionService {
  // Public Properties
  public userSessionPermissions: UsuarioPerfilResponse;
  // Private Properties
  private jsonWebToken: string;
  private cognitoUserSession: CognitoUserSession;

  constructor(
    private http: HttpService,
    private router: Router,
    private factory: FactoryService
  ) {}

  // Public Methods
  /**
   * Performs an HTTP request to the API and retrieve all permissions for the logged user
   * stored on the Bus Pay database.
   * @throws UserPermissionsException
   * @author Renan Souza
   * @version 1.0.0
   */
  public async getUserPermissionsFromDatabase(
    cognitoId: string,
    sistema?: SistemaEnum
  ): Promise<any> {
    try {
      const params = sistema? { cognitoId: cognitoId, sistema: sistema } : {cognitoId: cognitoId};
      const endpoint: string = "/UsuarioPerfil/PermissoesPerfilUsuario";
      const options = await this.factory.defaultHttpHeaders(params);
      const response = await this.http.get(endpoint, options);
      return response.data;
    } catch (error) {
      throw new UserPermissionsException(error.message);
    }
  }

  /**
   * Returns the logged user Json Web Token. This token is generated by cognito and
   * in order to use it, the user must be logged on cognito
   * @author Renan Souza
   * @version 1.0.0
   */
  public getSessionJwt(): string {
    return this.jsonWebToken;
  }

  /**
   * Method to save the logged user Json Web Token inside the local store
   * and also make it available inside the Session Service.
   * @param jsonWebToken string
   * @author Renan Souza
   * @version 1.0.0
   */
  public setSessionJwt(jsonWebToken: string): void {
    localStorage.setItem("jwt", jsonWebToken);
    this.jsonWebToken = jsonWebToken;
  }

  /**
   * Method to save the cognito user session inside the local storage
   * and also make it available inside the Session Service
   * @param session The cognito user session to be saved. In order o save this
   * the user must be authenticated on the aws cognito.
   * @author Renan Souza
   * @version 1.0.0
   */
  public setCognitoUserSession(session: CognitoUserSession): void {
    localStorage.setItem("cognitoUserSession", JSON.stringify(session));
    this.cognitoUserSession = session;
  }

  /**
   * Returns the logged user cognito session.
   * @author Renan Souza
   * @version 1.0.0
   */
  public getCognitoUserSession(): CognitoUserSession {
    return this.cognitoUserSession;
  }

  /**
   * Saves the user session containing all the user information and permissions
   * on the local storage and also make this available inside the service
   * @param userSession The logged user session with all the necessary permissions
   * @author Renan Souza
   * @version 1.0.0
   */
  public setUserSession(userSession: UsuarioPerfilResponse): void {
    localStorage.setItem("userSessionPermissions", JSON.stringify(userSession));
    this.userSessionPermissions = userSession;
  }

  /**
   * Return the user session with all the permissions
   * @author Renan Souza
   * @version 1.0.0
   */
  public getUserSession(): UsuarioPerfilResponse {
    return this.userSessionPermissions;
  }

  /**
   * Returns the logged user ID
   * @author Renan Souza
   * @version 1.0.0
   */
  public getUserId(): string {
    return this.userSessionPermissions.id;
  }

  /**
   * Clean all the data from the service and also clean everything
   * from the local storage.
   * @author Renan Souza
   * @version 1.0.0
   */
  public logout(): void {
    this.cognitoUserSession = null;
    this.userSessionPermissions = null;
    this.jsonWebToken = null;
    localStorage.clear();
    EventEmitterBaseService.get(AppEvents.hideApplicationNavigationBars).emit();
    this.router.navigate(["/login"]).catch((error) => console.log(error));
  }

  /**
   * Check if the user is logged on the service and on the local storage.
   * Returns this value.
   * @author Renan Souza
   * @version 1.0.0
   */
  public userIsLogged(): boolean {
    this.buildUserSessionFromLocalStorage();
    return !(
      !this.jsonWebToken ||
      !this.cognitoUserSession ||
      !this.userSessionPermissions
    );
  }

  /**
   * Read every single piece of data regarding the user session and build it on the
   * session to be used by this service.
   * All the data will be read from the local storage directly to the service.
   * @author Renan Souza
   * @version 1.0.0
   */
  public buildUserSessionFromLocalStorage(): void {
    this.jsonWebToken = localStorage.getItem("jwt");
    this.cognitoUserSession = JSON.parse(
      localStorage.getItem("cognitoUserSession")
    );
    this.userSessionPermissions = JSON.parse(
      localStorage.getItem("userSessionPermissions")
    );
  }

  /**
   * Check if the user is a Root user
   * @author Renan Souza
   * @version 1.0.0
   */
  public isRootUser(): boolean {
    this.buildUserSessionFromLocalStorage();
    return this.userSessionPermissions.root;
  }

  /**
   * Check if the user has 'bilhetadora' id
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasBilhetadoraId(): boolean {
    return !!this.userSessionPermissions.bilhetadoraId;
  }

  /**
   * Check if the user has 'Operadora' id
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasOperadoraId(): boolean {
    return !!this.userSessionPermissions.operadoras;
  }

  /**
   * Returns the logged user 'Bilhetadora' id
   * @author Renan Souza
   * @version 1.0.0
   */
  public getBilhetadoraId(): number {
    return this.userSessionPermissions.bilhetadoraId;
  }

  /**
   * Returns the logged user 'Operadora' Id
   * @author Renan Souza
   * @version 1.0.0
   */
  public getOperadoraId(): number {
    return this.userSessionPermissions.operadoras[0].id;
  }

  /**
   * Check if the player has 'consorcio' id
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasConsorcioId(): boolean {
    return !!this.userSessionPermissions.consorcioId;
  }

  /**
   * Return the logged user 'Consorcio' id
   * @author Renan Souza
   * @version 1.0.0
   */
  public getConsorcioId(): number {
    return this.userSessionPermissions.consorcioId;
  }

  /**
   * Validates if the user has a seller id and return this validations
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasLojaId(): boolean {
    return !!this.userSessionPermissions.lojaId;
  }

  /**
   * Retrieves the authenticated user seller id
   * @author Renan Souza
   * @version 1.0.0
   */
  public getLojaId(): number {
    return this.userSessionPermissions.lojaId;
  }

  /**
   * Validates if the user has a 'gestor' ID
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasGestorId(): boolean {
    return !!this.userSessionPermissions.gestorId;
  }

  /**
   * Retrieves the logged user gestor ID
   * @author Renan Souza
   * @version 1.0.0
   */
  public getGestorId(): number {
    return this.userSessionPermissions.gestorId;
  }

  /**
   * Validates if the user has a public entity ID
   * @author Renan Souza
   * @version 1.0.0
   */
  public hasPublicEntityId(): boolean {
    return !!this.userSessionPermissions.entidadePublicId;
  }

  /**
   * Retrieves the logged user gestor ID
   * @author Renan Souza
   * @version 1.0.0
   */
  public getPublicEntityId(): number {
    return this.userSessionPermissions.entidadePublicId;
  }

  // /**
  //  * Validates if the logged user has the necessary permissions to see some menu links.
  //  * The user must have permissions to access the System and desired Menu.
  //  * Returns false if the user don't have any of these permissions.
  //  * Returns true if the user have all the necessary permissions.
  //  * @param systemMenuPermissionEnum The menu that holds this action
  //  * @author Renan Souza
  //  * @version 1.0.2
  //  */
  // public hasSystemMenuPermission(systemMenuPermissionEnum: MenuSistemaEnum): boolean {
  //   if (this.isRootUser()) return true;

  //   const systemPermission = this.userSessionPermissions.usuarioPerfilSistemas.find(permission => {
  //     if (UserSystemPermissionsEnum.BILHETAGEM === permission.sistemaID) return permission;
  //   });

  //   if (!systemPermission) return false;

  //   const menuPermission = systemPermission.usuarioPerfilMenus.find(permission => {
  //     if (systemMenuPermissionEnum === permission.menuID) return permission;
  //   });

  //   return !!menuPermission;
  // }

  /**
   * Validates if the logged user has the necessary permissions to perform some actions.
   * The user must have permissions to access the System, the Menu and, therefore, the action.
   * Returns false if the user don't have any of these permissions.
   * Returns true if the user have all the necessary permissions
   * @param actionPermissionEnum The action the user is trying to perform
   * @param systemMenuPermissionEnum The menu that holds this action
   * @author Renan Souza
   * @version 1.0.2
   */
  public hasMenuActionPermission(
    actionPermissionEnum: ActionMenuPermissionsEnum,
    systemMenuPermissionEnum: MenuSistemaEnum
  ): boolean {
    if (this.isRootUser()) return true;

    const systemPermission =
      this.userSessionPermissions.usuarioPerfilSistemas.find((permission) => {
        if (SistemaEnum.Bilhetagem === permission.sistemaID) return permission;
      });

    if (!systemPermission) return false;

    const menuPermission = systemPermission.usuarioPerfilMenus.find(
      (permission) => {
        if (systemMenuPermissionEnum === permission.menuID) return permission;
      }
    );

    if (!menuPermission) return false;

    const actionPermission = menuPermission.usuarioPerfilAcoes.find(
      (permission) => {
        if (actionPermissionEnum === permission.acaoID) return permission;
      }
    );

    return !!actionPermission;
  }
  public getNomeUsuario() {
    return this.userSessionPermissions.nomeUsuario;
  }
}
